import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { HR_PERMISSIONS, hasHrFeature, hasHrPermission } from "../../constants/hrPermissions";
import EmployeeDetailsTab from "./EmployeeDetailsTab";
import EmployeeDocuments from "./EmployeeDocuments";
import EmployeeExitTab from "./EmployeeExitTab";
import EmployeeJobTab from "./EmployeeJobTab";
import EmployeeSalaryTab from "./payroll/EmployeeSalaryTab";
import { CURRENCY } from "./payroll/payrollHelpers";
import { BalanceCards } from "./LeaveMy";
import { FormFields, cleanValues, withNulls } from "./hrForm";
import { SIGN_IN_HINT, canEditLoginRole, employmentFields, loginFollowsProfile, personalFields, roleChangeOptions, signInChangeNotice } from "./employeeFields";
import { DetailGrid, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs } from "./hrComponents";
import { useHr } from "./HrGate";
import RoleControl from "./RoleControl";
import { cardStyle, formatDate, formatDays, formatRange, pageStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "./hrUi";

const editableKeys = () => [...personalFields(), ...employmentFields()].map((f) => f.key);

function EditDrawer({ show, onHide, employee, employeeId, onDone, canEditRole = false }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const showRole = canEditRole;
  const roleField = { key: "role", label: "Role", type: "select", options: roleChangeOptions(), hint: "What this login can do on the platform. Applies from their next sign-in." };
  // Work email / phone are also the sign-in for Admin, Support and HR logins.
  const withSignInHint = (f) => (loginFollowsProfile(employee.login_role) && (f.key === "work_email" || f.key === "work_phone") ? { ...f, hint: SIGN_IN_HINT } : f);
  useEffect(() => {
    if (show) {
      setValues({
        ...Object.fromEntries(editableKeys().filter((k) => employee[k] !== null && employee[k] !== undefined).map((k) => [k, employee[k]])),
        ...(canEditRole ? { role: Number(employee.login_role) } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const save = async () => {
    setSaving(true);
    try {
      const keys = editableKeys().filter((k) => k !== "date_of_joining");
      const body = withNulls(values, keys);
      // The role goes with the save only when it was actually changed.
      if (showRole && Number(values.role) !== Number(employee.login_role)) body.role = Number(values.role);
      const res = await hrmsService.updateEmployee(employeeId, body);
      if (res?.success) {
        toast.success([res.message || "Saved", signInChangeNotice(res)].filter(Boolean).join(" "));
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Edit employee"
      width={600}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      {showRole && Number(values.role) === 2 && Number(employee.login_role) !== 2 ? (
        <div className="mb-3" style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 8, padding: "10px 14px" }} role="alert">
          "Admin" gives this person full administrator access to your whole account - every screen, not just HR.
        </div>
      ) : null}
      <FormFields columns={2} values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={[...(showRole ? [roleField] : []), ...[...personalFields(), ...employmentFields().filter((f) => f.key !== "date_of_joining")].map(withSignInHint)]} />
      <div style={{ fontSize: 12, color: "#6B7280" }}>The date of joining and employee code are fixed once set. Job details change through Job history.</div>
    </SlidePanel>
  );
}

// A person asks HR to change their own address or personal details; it goes through approval.
function ChangeRequestDrawer({ show, onHide, employeeId, employee, onDone }) {
  const [kind, setKind] = useState("ADDRESS");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show) {
      setKind("ADDRESS");
      setValues({});
    }
  }, [show]);
  const fields = kind === "ADDRESS" ? ["current_address", "permanent_address"] : ["marital_status", "work_phone", "nationality"];
  const descriptors = personalFields().filter((f) => fields.includes(f.key));
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.requestProfileChange(employeeId, { kind, values: cleanValues(values) });
      if (res?.success) {
        toast.success(res.message || "Sent for approval");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Request a change"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Send for approval
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={{ kind, ...values }}
        onChange={(k, v) => (k === "kind" ? (setKind(v), setValues({})) : setValues((s) => ({ ...s, [k]: v })))}
        fields={[{ key: "kind", label: "What do you want to change", type: "select", options: [{ label: "Address", value: "ADDRESS" }, { label: "Personal details", value: "PERSONAL" }] }, ...descriptors]}
      />
      <div style={{ fontSize: 12, color: "#6B7280" }}>Currently: {fields.map((f) => employee[f] || "—").join(" · ")}</div>
    </SlidePanel>
  );
}

// EMP-09: the extra fields this company tracks. HR fills them in; a file-type field is managed through Documents.
function CustomFieldsDrawer({ show, onHide, fields, employeeId, onDone }) {
  const editable = fields.filter((f) => f.field_type !== "FILE");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show) setValues(Object.fromEntries(editable.map((f) => [f.field_key, f.value ?? ""])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const descriptors = editable.map((f) => ({
    key: f.field_key,
    label: f.label,
    required: !!f.is_required,
    type: f.field_type === "DROPDOWN" ? "select" : f.field_type === "NUMBER" ? "number" : f.field_type === "DATE" ? "date" : "text",
    options: (Array.isArray(f.options) ? f.options : []).map((o) => ({ label: o, value: o })),
  }));
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.setCustomValues(employeeId, values);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Additional details"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <FormFields values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={descriptors} />
    </SlidePanel>
  );
}

function LeaveTab({ employeeId }) {
  const bal = useLoad(() => hrmsService.getLeaveBalance({ employee_id: employeeId }), [employeeId]);
  const reqs = useLoad(() => hrmsService.listLeaveRequests({ employee_id: employeeId }), [employeeId]);
  return (
    <>
      <LoadState loading={bal.loading} error={bal.error} onRetry={bal.reload} rows={2}>
        <BalanceCards balances={bal.data?.balances || []} />
      </LoadState>
      <h6 className="mt-4 mb-2">Requests</h6>
      <LoadState loading={reqs.loading} error={reqs.error} onRetry={reqs.reload}>
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table className="table mb-0">
            <thead>
              <tr>
                {["Type", "Dates", "Days", "Status"].map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(reqs.data || []).map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.leave_type_name}</td>
                  <td style={tdStyle}>{formatRange(r.from_date, r.to_date)}</td>
                  <td style={tdStyle}>{formatDays(r.total_days)}</td>
                  <td style={tdStyle}>
                    <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>
    </>
  );
}

// EMP-04: the employee record. HR sees and edits everything; a manager sees their reports; a person sees themself.
export default function EmployeeProfile({ mine = false }) {
  const params = useParams();
  const { access } = useHr();
  const load = useLoad(() => (mine ? hrmsService.getMyProfile() : hrmsService.getEmployee(params.id)), [mine, params.id]);
  const settings = useLoad(() => hrmsService.getSettings(), []);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [editingCustom, setEditingCustom] = useState(false);

  const profile = load.data;
  const employee = profile?.employee;
  const employeeId = employee?.employee_id;
  const canManage = hasHrPermission(access, HR_PERMISSIONS.HR_ADMIN);
  const isSelf = !!profile?.access?.is_self;
  const country = settings.data?.country_pack;
  // Salary is the most sensitive data here: payroll administrators only, and only where the payroll add-on is on.
  const canPayroll = !mine && !isSelf && hasHrFeature(access, "PAYROLL") && hasHrPermission(access, HR_PERMISSIONS.PAYROLL_ADMIN);

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "job", label: "Job history" },
    { key: "details", label: "Bank & contacts" },
    { key: "documents", label: "Documents" },
    { key: "leave", label: "Leave" },
    ...(canPayroll ? [{ key: "salary", label: "Salary" }] : []),
    { key: "exit", label: "Exit" },
  ];

  return (
    <div style={pageStyle}>
      {!mine ? (
        <div className="mb-2" style={{ fontSize: 13 }}>
          <Link to="/hr/employees">← Employees</Link>
        </div>
      ) : null}
      <LoadState loading={load.loading} error={load.error} onRetry={load.reload} rows={6}>
        {profile ? (
          <>
            <PageHeader
              title={employee.full_name}
              subtitle={[employee.employee_code, employee.designation_name, employee.department_name].filter(Boolean).join(" · ")}
              actions={
                <>
                  <span style={{ ...statusBadge(employee.status), alignSelf: "center" }}>{statusLabel(employee.status)}</span>
                  {canManage ? <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton> : null}
                  {isSelf && !canManage ? <SecondaryButton onClick={() => setRequesting(true)}>Request a change</SecondaryButton> : null}
                </>
              }
            />
            <Tabs tabs={tabs} active={tab} onChange={setTab} />

            {tab === "overview" ? (
              <div style={{ ...cardStyle, padding: 20 }}>
                <DetailGrid
                  columns={3}
                  items={[
                    ["Login role", <RoleControl key="role" employeeId={employeeId} role={employee.login_role} canChange={!mine && !!access.is_tenant_admin} onDone={load.reload} />],
                    ["Work email", employee.work_email],
                    ["Work phone", employee.work_phone],
                    ["Gender", statusLabel(employee.gender)],
                    ["Date of birth", formatDate(employee.date_of_birth)],
                    ["Nationality", employee.nationality],
                    ["Marital status", statusLabel(employee.marital_status)],
                    ["Date of joining", formatDate(employee.date_of_joining)],
                    ["Probation ends", employee.probation_end_date ? formatDate(employee.probation_end_date) : null],
                    ["Confirmation", employee.confirmation_date ? formatDate(employee.confirmation_date) : null],
                    ["Department", employee.department_name],
                    ["Designation", employee.designation_name],
                    ["Grade", employee.grade_name],
                    ["Location", employee.location_name],
                    ["Site", employee.site_name],
                    ["Cost centre", employee.cost_centre_name],
                    ["Reports to", employee.manager_name],
                    ["Employment type", statusLabel(employee.employment_type)],
                    ["Leave policy", employee.leave_policy_name],
                    ["Notice period", employee.notice_period_days ? `${employee.notice_period_days} days` : null],
                    ["Current address", employee.current_address],
                    ["Permanent address", employee.permanent_address],
                  ]}
                />
                {(profile.custom_fields || []).length ? (
                  <>
                    <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
                      <h6 className="mb-0">Additional details</h6>
                      {canManage ? (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => setEditingCustom(true)}>
                          Edit
                        </button>
                      ) : null}
                    </div>
                    <DetailGrid columns={3} items={profile.custom_fields.map((f) => [f.label || f.name, f.value])} />
                  </>
                ) : null}
              </div>
            ) : null}
            {tab === "job" ? <EmployeeJobTab employeeId={employeeId} canManage={canManage} /> : null}
            {tab === "details" ? <EmployeeDetailsTab employeeId={employeeId} profile={profile} countryPack={country} reload={load.reload} /> : null}
            {tab === "documents" ? <EmployeeDocuments employeeId={employeeId} canManage={canManage} isSelf={isSelf} /> : null}
            {tab === "leave" ? <LeaveTab employeeId={employeeId} /> : null}
            {tab === "salary" && canPayroll ? <EmployeeSalaryTab employeeId={employeeId} currency={CURRENCY[country]} /> : null}
            {tab === "exit" ? <EmployeeExitTab employeeId={employeeId} employee={employee} isSelf={isSelf} canManage={canManage} reload={load.reload} /> : null}

            <EditDrawer
              show={editing}
              onHide={() => setEditing(false)}
              employee={employee}
              employeeId={employeeId}
              onDone={load.reload}
              canEditRole={!mine && canEditLoginRole({ isTenantAdmin: access.is_tenant_admin, loginRole: employee.login_role, status: employee.status, employeeId })}
            />
            <CustomFieldsDrawer show={editingCustom} onHide={() => setEditingCustom(false)} fields={profile.custom_fields || []} employeeId={employeeId} onDone={load.reload} />
            <ChangeRequestDrawer show={requesting} onHide={() => setRequesting(false)} employee={employee} employeeId={employeeId} onDone={load.reload} />
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
